import classNames from 'classnames';
import { IconV1 } from 'common/icon/iconV1.js';
import { SEARCH_BAR_DEFAULT_TEXT } from 'components/search-bar/search-bar-utils.js';
import { SearchContent } from 'components/search-overlay/search-content.js';
import debounce from 'lodash.debounce';
import { StickyPosition } from 'page-context/scrollStickyStateProvider/scrollStickyMain.js';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { REDUX_APP_SET_OVERLAY_CLOSED } from 'redux-state/app/reducer.js';
import { useAppDispatch } from 'redux-state/configure-store.js';
import { IRepoState } from 'redux-state/rootReducer.js';
import {
  REDUX_SEARCH_ASYNC,
  REDUX_SEARCH_CLEAR_RESULTS,
  REDUX_SEARCH_CLOSE_OVERLAY,
} from 'redux-state/search/reducer.js';
import { SEARCH_THRESHOLD } from 'redux-state/search/types.js';
import { usePrevious } from 'utilities/hooks/use-previous.js';
import { useNormalisedPathname } from 'utilities/router/api-fetch-path';
import { getWindow } from 'utilities/window.js';

const DEBOUNCED_SEARCH_TIME = 500;

export const SearchOverlay = () => {
  const appDispatch = useAppDispatch();
  const { clearDynamicBarPos } = React.useContext(StickyPosition);
  const displaySearchOverlay = useSelector((state: IRepoState) => state.SEARCH.displaySearchOverlay);
  const [displaySearchOverlayIsOpen, setDisplaySearchOverlayIsOpen] = useState(displaySearchOverlay);
  const prevDisplaySearchOverlayIsOpen = usePrevious(displaySearchOverlayIsOpen);

  const routing = useNormalisedPathname();
  const prevRouting = usePrevious(routing);

  const offsetTop = useSelector((state: IRepoState) => state.SEARCH.offsetTop);
  const prevOffsetTop = usePrevious(offsetTop);

  const container = useRef<HTMLDivElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const scrollY = useRef(0);
  const [inputValue, setInputValue] = useState('');

  const closeOverlay = React.useCallback(() => {
    setDisplaySearchOverlayIsOpen(false);
    appDispatch(REDUX_APP_SET_OVERLAY_CLOSED());
    appDispatch(REDUX_SEARCH_CLEAR_RESULTS());
    searchInput.current?.blur();
    setInputValue('');
  }, [appDispatch]);

  const handleKeyPress = React.useCallback(
    (e: KeyboardEvent) => {
      if (['Escape', 'Esc'].includes(e.key)) {
        appDispatch(REDUX_SEARCH_CLOSE_OVERLAY());
      }
    },
    [appDispatch],
  );

  const search = React.useMemo(
    () =>
      debounce((query: string) => {
        appDispatch(REDUX_SEARCH_CLEAR_RESULTS());
        appDispatch(REDUX_SEARCH_ASYNC({ query }));
      }, DEBOUNCED_SEARCH_TIME),
    [appDispatch],
  );

  const handleChange = React.useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      event.stopPropagation();
      const value = event.currentTarget.value;
      setInputValue(value);
      value.length >= SEARCH_THRESHOLD ? search(value) : appDispatch(REDUX_SEARCH_CLEAR_RESULTS());
    },
    [appDispatch, search],
  );

  useEffect(() => {
    getWindow()?.addEventListener('keydown', handleKeyPress);
    return () => getWindow()?.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  useEffect(() => {
    const lclWindow = getWindow();
    if (displaySearchOverlay) {
      scrollY.current = lclWindow?.scrollY ?? 0;
      setDisplaySearchOverlayIsOpen(true);
      setTimeout(() => lclWindow?.scrollTo(0, 0));
    } else if (prevDisplaySearchOverlayIsOpen) {
      lclWindow?.scrollTo(0, scrollY.current);
      requestAnimationFrame(() => {
        clearDynamicBarPos();
        lclWindow?.scrollTo(0, (lclWindow?.scrollY ?? 0) - 1);
        setTimeout(closeOverlay);
      });
    }
  }, [displaySearchOverlay, closeOverlay, prevDisplaySearchOverlayIsOpen, clearDynamicBarPos]);

  useEffect(() => {
    if (displaySearchOverlayIsOpen) {
      searchInput.current?.focus();
      container.current?.scrollTo?.(0, 0);
    }
  }, [displaySearchOverlayIsOpen]);

  useEffect(() => {
    if (routing !== prevRouting) {
      setInputValue('');
    }
  }, [routing]);

  return (
    <div
      data-comp="search-overlay"
      style={{
        top: `${displaySearchOverlayIsOpen ? prevOffsetTop || '0' : offsetTop}px`,
        height: `calc(100% - ${offsetTop}px)`,
      }}
      className={classNames('SearchOverlay', { 'is-open': displaySearchOverlayIsOpen })}
      ref={container}
    >
      <div className="u-mB-md bg-white">
        <div className="Container">
          <div className="SearchBar SearchBar--overlay">
            <div className="SearchBar-input-container">
              <div className="SearchBar-actions">
                <input
                  type="text"
                  className="SearchBar-actions-find"
                  value={inputValue}
                  onChange={handleChange}
                  placeholder={SEARCH_BAR_DEFAULT_TEXT}
                  ref={searchInput}
                />
                <div className="SearchBar-actions-icon">
                  <IconV1 icon="search" />
                </div>
              </div>
            </div>
            <button
              className="icon--hover-link icon--hover-scale"
              onClick={() => appDispatch(REDUX_SEARCH_CLOSE_OVERLAY())}
            >
              <IconV1 iconClass="svg--fill-inherit svg--1em" icon="exit" />
            </button>
          </div>
        </div>
      </div>

      <div className="Container">
        <SearchContent text={inputValue} />
      </div>
    </div>
  );
};
